import { useEffect, useRef, useCallback } from "react";

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "focus",
] as const;

interface ActivityTrackerOptions {
  pingIntervalMinutes?: number;
  inactivityTimeoutMinutes?: number;
  onActivityPing?: () => Promise<void> | void;
  enableLogging?: boolean;
}

export const useUserActivityTracker = ({
  pingIntervalMinutes = 5,
  inactivityTimeoutMinutes = 5,
  onActivityPing,
  enableLogging = false,
}: ActivityTrackerOptions = {}) => {
  const lastActivityTime = useRef<number>(Date.now());
  const lastPingTime = useRef<number>(0);
  const pingIntervalId = useRef<NodeJS.Timeout | null>(null);
  const isUserActive = useRef<boolean>(true);

  const LAST_ACTIVITY_KEY = "user_last_activity";
  const LAST_PING_KEY = "user_last_ping";

  const isActiveInLastMinutes = useCallback((minutes: number): boolean => {
    const now = Date.now();
    const timeDiff = now - lastActivityTime.current;
    const minutesDiff = timeDiff / (1000 * 60);
    return minutesDiff <= minutes;
  }, []);

  const log = useCallback(
    (message: string): void => {
      if (enableLogging) {
        console.log(`[ActivityTracker] ${message}`);
      }
    },
    [enableLogging]
  );

  useEffect(() => {
    const getFromStorage = (key: string): number => {
      try {
        const value = localStorage.getItem(key);
        if (value) {
          const parsed = parseInt(value, 10);
          return isNaN(parsed) ? 0 : parsed;
        }
      } catch (error) {
        log(`Error reading ${key}`);
      }
      return 0;
    };

    const setToStorage = (key: string, value: number): void => {
      try {
        localStorage.setItem(key, value.toString());
      } catch (error) {
        log(`Error writing ${key}`);
      }
    };

    const updateActivity = (): void => {
      const now = Date.now();
      lastActivityTime.current = now;
      isUserActive.current = true;
      setToStorage(LAST_ACTIVITY_KEY, now);
      log("Activity updated");
    };

    const performPing = async (): Promise<void> => {
      const now = Date.now();
      const timeSinceLastPing = now - lastPingTime.current;
      const minutesSinceLastPing = timeSinceLastPing / (1000 * 60);

      if (minutesSinceLastPing < pingIntervalMinutes) {
        log(`Ping skipped - not enough time has passed`);
        return;
      }

      const userIsActive = isActiveInLastMinutes(inactivityTimeoutMinutes);
      isUserActive.current = userIsActive;

      if (!userIsActive) {
        log("Ping skipped - user is not active");
        return;
      }

      if (!onActivityPing) {
        log("Ping skipped - no callback function");
        return;
      }

      try {
        log("Sending ping...");

        lastPingTime.current = now;
        setToStorage(LAST_PING_KEY, now);

        const result = onActivityPing();

        if (result instanceof Promise) {
          await result;
        }

        log("Ping sent");
      } catch (error) {
        log("Error sending ping");

        const previousPingTime = getFromStorage(LAST_PING_KEY);
        lastPingTime.current = previousPingTime;
      }
    };

    const storedActivity = getFromStorage(LAST_ACTIVITY_KEY);
    const storedPing = getFromStorage(LAST_PING_KEY);

    if (storedActivity > 0) {
      lastActivityTime.current = storedActivity;
      log(`Loaded last activity from storage: ${new Date(storedActivity)}`);
    } else {
      updateActivity();
    }

    if (storedPing > 0) {
      lastPingTime.current = storedPing;
      log(`Loaded last ping from storage: ${new Date(storedPing)}`);
    }

    let activityThrottleTimeout: NodeJS.Timeout | null = null;
    const throttledUpdateActivity = (): void => {
      if (activityThrottleTimeout) return;

      activityThrottleTimeout = setTimeout(() => {
        updateActivity();
        activityThrottleTimeout = null;
      }, 30000);
    };

    ACTIVITY_EVENTS.forEach((eventType) => {
      document.addEventListener(eventType, throttledUpdateActivity, {
        passive: true,
      });
    });

    const handleVisibilityChange = async (): Promise<void> => {
      const isVisible = !document.hidden;

      if (isVisible) {
        log("Page became visible - user switched to this tab");
        updateActivity();
        performPing();
      } else {
        log("Page became hidden - user switched away from this tab");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const handleWindowFocus = (): void => {
      log("Window gained focus");
      updateActivity();
      performPing();
    };

    window.addEventListener("focus", handleWindowFocus);

    pingIntervalId.current = setInterval(() => {
      performPing().catch(() => {
        log("Unexpected error in ping interval");
      });
    }, pingIntervalMinutes * 60 * 1000);

    return () => {
      ACTIVITY_EVENTS.forEach((eventType) => {
        document.removeEventListener(eventType, throttledUpdateActivity);
      });

      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);

      if (pingIntervalId.current) {
        clearInterval(pingIntervalId.current);
        pingIntervalId.current = null;
      }

      if (activityThrottleTimeout) {
        clearTimeout(activityThrottleTimeout);
      }
    };
  }, [
    pingIntervalMinutes,
    inactivityTimeoutMinutes,
    enableLogging,
    isActiveInLastMinutes,
    onActivityPing,
    log,
  ]);

  const updateActivity = useCallback((): void => {
    const now = Date.now();
    lastActivityTime.current = now;
    isUserActive.current = true;
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
      log("Activity updated");
    } catch (error) {
      log("Error writing activity");
    }
  }, [log]);

  return {
    updateActivity,
    isActiveInLastMinutes,
  };
};
